import { MMButton } from '@components'
import cn from 'classnames'
import { isSafeTextArea, tr } from 'mmfintech-commons'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { ALLOWED_ATTACHMENT_TYPES } from '../../utils'
import styles from './ComposeArea.module.scss'

const FORBIDDEN_CHARS_HINT_DURATION_MS = 2500

export interface ComposeAreaHandle {
  openFilePicker: () => void
}

interface Props {
  text: string
  onTextChange: (value: string) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
  files: File[]
  fileErrors: string[]
  onDismissFileErrors?: () => void
  onRemoveFile: (index: number) => void
  onAddFiles: (files: File[]) => void
  placeholder?: string
  maxLength?: number
  disabled?: boolean
  textareaClassName?: string
  inlineButtons?: React.ReactNode
}

const ComposeArea = forwardRef<ComposeAreaHandle, Props>(
  (
    {
      text,
      onTextChange,
      onKeyDown,
      files,
      fileErrors,
      onDismissFileErrors,
      onRemoveFile,
      onAddFiles,
      placeholder,
      maxLength,
      disabled,
      textareaClassName = 'compose-textarea',
      inlineButtons
    },
    ref
  ) => {
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [showForbiddenCharsHint, setShowForbiddenCharsHint] = useState(false)
    const forbiddenCharsHintTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

    useImperativeHandle(ref, () => ({
      openFilePicker: () => fileInputRef.current?.click()
    }))

    useEffect(() => () => clearTimeout(forbiddenCharsHintTimeoutRef.current), [])

    // isSafeTextArea (mmfintech-commons) validates the whole string against the same
    // character-whitelist rule the BE enforces, not a single character -- there's no reliable
    // way to "strip just the bad part" the way an earlier, hand-rolled character-blacklist
    // attempt did (and got wrong: it blocked '=' and ';', which are actually allowed). Reject the
    // whole edit and revert to the last valid value instead, matching how checkFinalValue/
    // formValues validation works elsewhere in this codebase (accept or reject the whole value,
    // not a partial edit).
    function handleTextChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
      const target = e.target
      const rawValue = target.value
      if (rawValue !== '' && !isSafeTextArea(rawValue.normalize('NFKC'))) {
        const caret = target.selectionStart ?? rawValue.length
        const insertedCount = rawValue.length - text.length
        const revertedCaret = Math.max(0, caret - insertedCount)
        target.value = text
        target.setSelectionRange(revertedCaret, revertedCaret)
        setShowForbiddenCharsHint(true)
        clearTimeout(forbiddenCharsHintTimeoutRef.current)
        forbiddenCharsHintTimeoutRef.current = setTimeout(
          () => setShowForbiddenCharsHint(false),
          FORBIDDEN_CHARS_HINT_DURATION_MS
        )
        return
      }
      onTextChange(rawValue)
    }

    return (
      <>
        {fileErrors.length > 0 && (
          <div className={cn(styles['compose-file-errors'])}>
            {fileErrors.map(err => (
              <div key={err} className={cn(styles['compose-file-error'])}>
                {err}
              </div>
            ))}
            {onDismissFileErrors && (
              <MMButton
                type='button'
                variant='secondary'
                collapsed
                collapsedIcon={<span>×</span>}
                title={tr('FRONTEND.BUTTONS.CLOSE', 'Close')}
                className={cn(styles['compose-file-errors-dismiss'])}
                onClick={onDismissFileErrors}
              />
            )}
          </div>
        )}

        {files.length > 0 && (
          <div className={cn(styles['compose-attachments'])}>
            {files.map((f, i) => (
              <span key={f.name} className={cn(styles['compose-attachment-chip'])}>
                📎 {f.name}
                <MMButton
                  type='button'
                  variant='secondary'
                  collapsed
                  collapsedIcon={<span>×</span>}
                  title={tr('FRONTEND.BUTTONS.REMOVE', 'Remove')}
                  className={cn(styles['compose-remove-file'])}
                  onClick={() => onRemoveFile(i)}
                />
              </span>
            ))}
          </div>
        )}

        {inlineButtons ? (
          <div className={cn(styles['compose-inline-row'])}>
            <textarea
              className={textareaClassName}
              value={text}
              onChange={handleTextChange}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              maxLength={maxLength}
              disabled={disabled}
            />
            {inlineButtons}
          </div>
        ) : (
          <textarea
            className={textareaClassName}
            value={text}
            onChange={handleTextChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            maxLength={maxLength}
            disabled={disabled}
          />
        )}

        {showForbiddenCharsHint && (
          <div className={cn(styles['compose-forbidden-chars-hint'])}>
            {tr('FRONTEND.SUPPORT.ERROR.FORBIDDEN_CHARS', "Some characters aren't allowed and were removed.")}
          </div>
        )}

        <input
          ref={fileInputRef}
          type='file'
          multiple
          accept={ALLOWED_ATTACHMENT_TYPES.join(',')}
          style={{ display: 'none' }}
          onChange={e => {
            if (e.target.files) {
              onAddFiles(Array.from(e.target.files))
              e.target.value = ''
            }
          }}
        />
      </>
    )
  }
)

ComposeArea.displayName = 'ComposeArea'

export default ComposeArea
